// NOTE: connector_type is intentionally named without "http" to remain
// extensible — future connector types (GRAPHQL, WEBSOCKET, etc.) can be added
// as new enum values without requiring a schema change.
export enum ECustomConnectorType {
  HTTP = "HTTP",
}

export enum ECustomAuthType {
  API_KEY_HEADER = "API_KEY_HEADER",
  BEARER_TOKEN = "BEARER_TOKEN",
  BASIC_AUTH = "BASIC_AUTH",
}

export enum ECustomToolMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

export enum ECustomParamType {
  STRING = "string",
  NUMBER = "number",
  BOOLEAN = "boolean",
  ARRAY = "array",
  OBJECT = "object",
}

export enum ECustomParamLocation {
  PATH = "path",
  QUERY = "query",
  BODY = "body",
}

// NOTE: Steps of the custom integration builder wizard, in the order they are
// presented. Numbered from 1 so a step can be compared with `<` to decide
// whether the user is allowed to jump back to it.
export enum ECustomIntegrationBuilderStep {
  CONNECTION = 1,
  TOOLS = 2,
  REVIEW = 3,
}

// NOTE: Recursive child param — used for nested OBJECT/ARRAY parameters up to
// MAX_PARAM_DEPTH levels. No `location` field; children inherit their parent's
// location for HTTP serialisation purposes.
export interface ICustomToolParamChild {
  name: string;
  type: ECustomParamType;
  required: boolean;
  description: string;
  defaultValue?: string | undefined;
  itemType?: ECustomParamType | undefined;
  children?: ICustomToolParamChild[] | undefined;
}

export interface ICustomToolParam {
  name: string;
  type: ECustomParamType;
  location: ECustomParamLocation;
  required: boolean;
  description: string;
  defaultValue?: string | undefined;
  // NOTE: Only meaningful when type = ARRAY. Describes the element type so the
  // LLM receives a precise Zod schema instead of z.array(z.unknown()).
  itemType?: ECustomParamType | undefined;
  // NOTE: Used when type = OBJECT or (type = ARRAY + itemType = OBJECT).
  // Declares named child params so the LLM receives z.object({...}) instead of
  // the open-ended z.record(z.string(), z.unknown()). Supports up to 3 levels
  // of nesting (root param counts as level 1).
  children?: ICustomToolParamChild[] | undefined;
}

export interface ICustomIntegration {
  uuid: string;
  name: string;
  connectorType: ECustomConnectorType;
  baseUrl: string;
  authType: ECustomAuthType;
  authHeaderName: string | null;
  isActive: boolean;
  isConnected: boolean;
  openapiSpecUrl: string | null;
  toolCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ICustomIntegrationTool {
  uuid: string;
  integrationUuid: string;
  toolName: string;
  displayName: string;
  description: string;
  method: ECustomToolMethod;
  pathTemplate: string;
  inputParams: ICustomToolParam[];
  createdAt: string;
  updatedAt: string;
}

export interface ICustomIntegrationTestResult {
  success: boolean;
  statusCode: number | null;
  latencyMs: number | null;
  error: string | null;
}

export interface IOpenApiParsedTool {
  toolName: string;
  displayName: string;
  description: string;
  method: ECustomToolMethod;
  pathTemplate: string;
  inputParams: ICustomToolParam[];
  warning: string | null;
}

export interface IOpenApiParsedResult {
  name: string;
  baseUrl: string;
  tools: IOpenApiParsedTool[];
  isTruncated: boolean;
}

export interface ICreateCustomIntegrationBody {
  name: string;
  connectorType: ECustomConnectorType;
  baseUrl: string;
  authType: ECustomAuthType;
  authHeaderName?: string | undefined;
  tools?: ICreateCustomToolBody[] | undefined;
}

export interface IUpdateCustomIntegrationBody {
  name?: string | undefined;
  baseUrl?: string | undefined;
  authType?: ECustomAuthType | undefined;
  authHeaderName?: string | null | undefined;
  isActive?: boolean | undefined;
  tools?: IUpsertCustomToolBody[] | undefined;
}

export interface ICreateCustomToolBody {
  toolName: string;
  displayName: string;
  description: string;
  method: ECustomToolMethod;
  pathTemplate: string;
  inputParams: ICustomToolParam[];
}

// NOTE: uuid identifies an existing tool to update/keep; omitting it signals a new insert.
export interface IUpsertCustomToolBody extends ICreateCustomToolBody {
  uuid?: string | undefined;
}

export interface IParseOpenApiSpecBody {
  specUrl?: string | undefined;
  specJson?: string | undefined;
}

export interface IUpdateCustomIntegrationParams {
  uuid: string;
  body: IUpdateCustomIntegrationBody;
}

export interface ICustomIntegrationUserConnection {
  isConnected: boolean;
}

export interface IConnectCustomIntegrationBody {
  authCredential?: string | null | undefined;
}

export interface IConnectCustomIntegrationParams {
  integrationUuid: string;
  body: IConnectCustomIntegrationBody;
}

export interface IDisconnectCustomIntegrationParams {
  integrationUuid: string;
}

// NOTE: Values of the builder's connection step. Mirrors the shape validated by
// `buildCustomIntegrationConnectionSchema`. `baseUrl` deliberately lives with
// the tools step instead, because an OpenAPI import auto-populates it.
export interface ICustomIntegrationConnectionFormValues {
  name: string;
  authType: ECustomAuthType;
  authHeaderName: string;
}

export interface ICustomToolFormValues {
  // NOTE: Present for existing saved tools so the backend can update in-place; absent for new tools (insert).
  uuid?: string | undefined;
  toolName: string;
  displayName: string;
  description: string;
  method: ECustomToolMethod;
  pathTemplate: string;
  inputParams: ICustomToolParam[];
}

export interface ICustomIntegrationToolsFormValues {
  tools: ICustomToolFormValues[];
}
